// apps/web/src/composables/useLicense.ts
//
// Pure frontend license verification (Ed25519) + periodic server-side check.
//
// License key format (base64url-encoded):
//   email|plan|expiry|ed25519_signature_hex
// where ed25519_signature is over the bytes of `${email}|${plan}|${expiry}`.
//
// The public key is safe to ship in the bundle. The private key lives only in
// the Cloudflare Worker that issues keys after Paddle checkout — it is never
// in the frontend, so licenses cannot be forged client-side.
//
// Periodic server check: every 24h (or on app start if last check was >24h
// ago), call /api/license?email=X. If 404 (revoked/canceled/refunded), clear
// localStorage + downgrade to Free. Network errors don't trigger downgrade.
import { verifyAsync } from '@noble/ed25519';
import { ref, computed } from 'vue';

// Public key (safe to expose in frontend bundle)
// Paired with EDD_PRIVATE_KEY stored in Cloudflare Pages secret
const PUBLIC_KEY_HEX = '4503e4a46177dffae60eb5b21c106f81c5160c419f042829c5fa4c0376a92dd8';

export type LicensePlan = 'monthly' | 'annual' | 'lifetime';
export interface License {
  email: string;
  plan: LicensePlan;
  expiry: string; // ISO date (YYYY-MM-DD)
  key: string;     // raw key for re-activation
}

const STORAGE_KEY = 'dumpdeck-license';
const LAST_CHECK_KEY = 'dumpdeck-license-last-check';
const VALID_PLANS: readonly LicensePlan[] = ['monthly', 'annual', 'lifetime'];

// Re-validate license every 6h. Network errors don't trigger downgrade
// (assume license still valid if we can't reach the server).
//
// Why 6h: Paddle webhook fires immediately on refund/cancel, but user's
// browser only sees the revoke when it next calls /api/license. 6h = fast
// enough to catch typical "next day" app reopen, slow enough to stay under
// KV free-tier limit (100k reads/day → 4 reads/user/day max, ~25k users OK).
// On tab visibility change (user returns to open tab), also re-validate if
// last check > 6h ago.
const VALIDATION_INTERVAL_MS = 6 * 60 * 60 * 1000;

// Hex to bytes
function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}

// Generate device fingerprint — SHA-256 hash of stable browser properties.
// Used for license abuse detection (same email activating from many fingerprints
// = likely license sharing). Stored in KV activate_log:{email} on the server.
//
// Stability: changes when user upgrades browser/OS or clears cookies — these
// are false positives. To reduce them, we use mostly-stable signals:
//   - User-Agent (browser + OS version)
//   - Screen dimensions (display)
//   - Timezone (region)
//   - Languages (preference)
//   - Canvas fingerprint (GPU + font rendering)
//
// Not 100% unique per device — multiple users with identical setup will share
// a fingerprint. That's fine; we're looking for **divergence** (many different
// fingerprints for one email = sharing), not uniqueness.
let _fingerprintCache: string | null = null;
export async function getDeviceFingerprint(): Promise<string> {
  if (_fingerprintCache) return _fingerprintCache;
  if (typeof window === 'undefined') return 'ssr';

  const parts: string[] = [];
  parts.push(navigator.userAgent);
  parts.push(`${screen.width}x${screen.height}x${screen.colorDepth}`);
  parts.push(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
  parts.push(navigator.languages?.join(',') || '');
  parts.push(navigator.platform || '');
  parts.push(navigator.hardwareConcurrency?.toString() || '');
  parts.push((navigator as any).deviceMemory?.toString() || '');

  // Canvas fingerprint — depends on GPU + font rendering, hard to spoof
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = "14px 'Arial'";
      ctx.fillText('DumpDeck fingerprint 🎯', 2, 2);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillRect(120, 5, 60, 30);
      parts.push(canvas.toDataURL());
    }
  } catch {
    // canvas blocked — skip
  }

  const encoded = new TextEncoder().encode(parts.join('|'));
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  const hashHex = Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  _fingerprintCache = hashHex;
  return hashHex;
}

// Base64 URL-safe decode
function base64UrlDecode(s: string): Uint8Array {
  let b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

// Verify a license key. Returns the parsed license if valid, null if invalid.
export async function verifyLicense(email: string, licenseKey: string): Promise<License | null> {
  try {
    const decoded = new TextDecoder().decode(base64UrlDecode(licenseKey));
    const parts = decoded.split('|');
    if (parts.length !== 4) return null;
    const [keyEmail, plan, expiry, sigHex] = parts;
    if (keyEmail !== email) return null;
    if (!VALID_PLANS.includes(plan as LicensePlan)) return null;
    if (!sigHex || sigHex.length !== 128) return null; // Ed25519 sig is 64 bytes = 128 hex chars

    const message = new TextEncoder().encode(`${email}|${plan}|${expiry}`);
    const signature = hexToBytes(sigHex);
    const publicKey = hexToBytes(PUBLIC_KEY_HEX);
    const valid = await verifyAsync(signature, message, publicKey);
    if (!valid) return null;

    return { email, plan: plan as LicensePlan, expiry, key: licenseKey };
  } catch {
    return null;
  }
}

// Store license in localStorage
export function storeLicense(license: License): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(license));
}

// Get license from localStorage (raw, unchecked for expiry)
export function getStoredLicense(): License | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// Check if license is valid AND not expired
export function isLicenseValid(license: License | null): boolean {
  if (!license) return false;
  const expiry = new Date(license.expiry);
  if (isNaN(expiry.getTime())) return false;
  const now = new Date();
  return expiry >= now;
}

// Reactive license state (shared across app — single source of truth)
const currentLicense = ref<License | null>(getStoredLicense());

// Last server-side validation result (cached in localStorage to throttle)
interface ServerLicenseInfo {
  email: string;
  plan: LicensePlan;
  expiry: string;
  // The stored license key from server — if it differs from localStorage,
  // it means plan was upgraded/downgraded. We update localStorage.
  license: string;
}

async function fetchServerLicense(email: string): Promise<ServerLicenseInfo | null> {
  try {
    const res = await fetch(`/api/license?email=${encodeURIComponent(email)}`);
    if (!res.ok) {
      if (res.status === 404) {
        // License revoked/canceled/refunded on server side
        return null;
      }
      // Other errors (500, network) — don't downgrade, assume valid
      console.warn('License check returned', res.status);
      return undefined as any; // sentinel for "couldn't verify"
    }
    const data = await res.json();
    return {
      email: data.email,
      plan: data.plan,
      expiry: data.expiry,
      license: data.license,
    };
  } catch (e) {
    console.warn('License check network error:', e);
    return undefined as any; // sentinel for network failure
  }
}

// Clear license from localStorage + reset reactive state.
// Defined at module scope so validateStoredLicense (also module scope) can use it.
function clearStoredLicense(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LAST_CHECK_KEY);
  currentLicense.value = null;
}

// Validate stored license against server.
// Called on app start (if last check > 24h ago) + periodically.
// Returns:
//   - 'valid'    → license still active on server
//   - 'revoked'  → license revoked (refund/cancel) — caller should clear
//   - 'updated'  → plan/expiry changed (re-activate with new key)
//   - 'unknown'  → network/server error — don't change anything
export async function validateStoredLicense(): Promise<'valid' | 'revoked' | 'updated' | 'unknown'> {
  return _validateStoredLicense(false);
}

// Force validate — bypasses the 6h throttle. Use before sensitive operations
// (file select, analyze) to catch revocations as soon as possible.
// CAUTION: increases KV reads — only call on user-initiated actions, not loops.
export async function forceValidateStoredLicense(): Promise<'valid' | 'revoked' | 'updated' | 'unknown'> {
  return _validateStoredLicense(true);
}

async function _validateStoredLicense(force: boolean): Promise<'valid' | 'revoked' | 'updated' | 'unknown'> {
  const stored = getStoredLicense();
  if (!stored) return 'valid'; // nothing to validate

  // Throttle: don't check more than once per 6h (unless forced)
  if (!force) {
    const lastCheck = parseInt(localStorage.getItem(LAST_CHECK_KEY) || '0', 10);
    if (Date.now() - lastCheck < VALIDATION_INTERVAL_MS) {
      return 'valid'; // recently checked, assume valid
    }
  }

  const serverInfo = await fetchServerLicense(stored.email);
  localStorage.setItem(LAST_CHECK_KEY, String(Date.now()));

  // Network error sentinel — don't downgrade
  if (serverInfo === undefined as any) return 'unknown';

  // Server says no license → revoked
  if (!serverInfo) {
    clearStoredLicense();
    return 'revoked';
  }

  // Plan/key changed on server → re-activate
  if (serverInfo.license !== stored.key) {
    const newLicense = await verifyLicense(serverInfo.email, serverInfo.license);
    if (newLicense) {
      storeLicense(newLicense);
      currentLicense.value = newLicense;
      return 'updated';
    }
  }

  return 'valid';
}

export function useLicense() {
  const isPro = computed(() => isLicenseValid(currentLicense.value));

  function setLicense(license: License) {
    storeLicense(license);
    currentLicense.value = license;
  }

  function clearLicense() {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LAST_CHECK_KEY);
    currentLicense.value = null;
  }

  // Activate by email only — fetch license from server, verify signature, write localStorage.
  // License key never appears in the UI; it's only in JS memory briefly during activation.
  // onProgress callback receives step descriptions for live UI feedback (builds user trust
  // by showing the multi-stage verification process).
  async function activate(
    email: string,
    onProgress?: (step: string) => void,
  ): Promise<{ success: boolean; error?: string }> {
    // 1. Generate device fingerprint — sent to server for abuse detection logging
    onProgress?.('Calculating device signature...');
    const fp = await getDeviceFingerprint();

    // 2. Look up license by email (server checks KV for active license)
    onProgress?.('Looking up license by email...');
    let serverData: { license: string; plan: string; expiry: string; email: string };
    try {
      const res = await fetch(`/api/license?email=${encodeURIComponent(email)}&fp=${encodeURIComponent(fp)}`);
      if (!res.ok) {
        if (res.status === 404) {
          return { success: false, error: 'No license found for this email. Please purchase Pro or contact support if you have already paid.' };
        }
        return { success: false, error: 'Could not verify license with server. Check your network and try again.' };
      }
      serverData = await res.json();
    } catch (e) {
      return { success: false, error: `Network error: ${(e as Error).message}` };
    }

    // 3. Verify license authenticity (cryptographic signature check)
    onProgress?.('Verifying license authenticity...');
    const license = await verifyLicense(email, serverData.license);
    if (!license) return { success: false, error: 'License verification failed. Please contact support.' };
    if (!isLicenseValid(license)) return { success: false, error: 'License expired. Please renew.' };

    // 4. Bind license to this browser
    onProgress?.('Securing license in browser...');
    setLicense(license);
    localStorage.setItem(LAST_CHECK_KEY, String(Date.now()));

    onProgress?.('Activated!');
    return { success: true };
  }

  return {
    currentLicense,
    isPro,
    activate,
    clearLicense,
    validateStoredLicense,
  };
}

